import { Box, Divider, HStack, useToast, VStack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { dateTimeZoneValues } from "features/Resources/Shared/Components/Fields/DateTimeZoneField.tsx/HorizontalDateTimeZoneField";
import { Meeting } from "features/Resources/Meeting/models";
import { setNewMeetingInQueryClient } from "features/Resources/Meeting/hooks";
import { apiMeetingById } from "helpers/url/api/v2";
import useAutoSave from "components/FormElements/useAutoSave";
import { useQueryClient } from "@tanstack/react-query";
import { COMMS_NO_ACTION_MENU_STATUSES, COMMS_NON_EDITABLE_STATUSES } from "features/Resources/Shared/constants";
import { removeLocalData } from "features/Resources/Shared/helpers";
import { FloatingField } from "design-system";
import MeetingHeader from "features/Resources/Shared/Components/MeetingHeader";
import UserField from "features/Resources/Shared/Components/Fields/UserField";
import MeetingLinksFields from "features/Resources/Meeting/Components/MeetingLinksFields";
import MeetingAttendeesField from "features/Resources/Meeting/Components/Fields/MeetingAttendeesField";
import MeetingThreeDotOptionMenu from "features/Resources/Meeting/Components/MeetingThreeDotMenu";
import { SchedulingShowButton } from "features/Resources/Meeting/Components/Button/Scheduling";
import ScheduledAtTag from "features/Resources/Meeting/Components/ScheduledAtTag";

function showScheduleMeetingButton(meeting: Meeting) {
  return ["pending_scheduling", "scheduled", "failed"].includes(meeting?.status);
}

interface MeetingFormProps {
  data: Meeting;
  includeHeader: boolean | undefined;
  includeMenu: boolean | undefined;
  isPending: boolean;
  includeStatus: boolean;
}

export default function MeetingForm({
  data,
  includeHeader = false,
  includeMenu = false,
  includeStatus = false,
  isPending,
}: MeetingFormProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { scheduledAt, ...rest } = data;
  const form = useForm<Meeting>({ defaultValues: { ...rest, ...dateTimeZoneValues(data, "scheduledAt") } });
  const { control } = form;
  const isDisabled = COMMS_NON_EDITABLE_STATUSES.includes(data.status || "");
  const showActionMenu = !COMMS_NO_ACTION_MENU_STATUSES.includes(data.status || "");

  const transformData = (updateData: Partial<Meeting>) => ({ meeting: removeLocalData(updateData) });

  useAutoSave({
    form,
    transformData,
    submitUrl: apiMeetingById(data?.id),
    onSuccess: (res: any) => {
      setNewMeetingInQueryClient(queryClient, res);
      toast({ title: "Meeting updated.", status: "success" });
    },
  });

  return (
    <VStack spacing={0.5} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between">
          <MeetingHeader form={form} meeting={data} isDisabled={isDisabled} />
          {data && includeMenu && showActionMenu && data.status !== "completed" && (
            <MeetingThreeDotOptionMenu meetingPrefixId={data.prefixId} />
          )}
        </HStack>
      )}
      <ScheduledAtTag
        scheduledAt={data?.scheduledAt as unknown as string}
        timezone={data?.timezone}
        durationMinutes={data?.durationMinutes}
        status={data?.status}
        mt={2}
      >
        {showScheduleMeetingButton(data) && <SchedulingShowButton meeting={data} />}
      </ScheduledAtTag>
      <Divider my="4" />
      {data && ["group_session_adhoc", "group_session_recurring"].includes(data.meetingType) && (
        <UserField form={form} fieldName="leadId" roleFieldName="leadRole" label="Lead" />
      )}
      <MeetingAttendeesField form={form} label="Attendees" fieldName="attendees" includeErrors />
      <MeetingLinksFields
        videoConferenceUrl={data.videoConferencingUrl}
        videoConferenceProvider={data.videoConferencingProvider}
        externalUrl={data.externalUrl}
      />
      <Box height={4} />
      {["group_session_adhoc", "group_session_recurring"].includes(data.meetingType) ? (
        <FloatingField name="learningObjective" errors={undefined}>
          <FloatingField.Label>Learning Objective</FloatingField.Label>
          <FloatingField.Trix control={control} name="learningObjective" hideToolbar />
        </FloatingField>
      ) : (
        <FloatingField name="purpose" errors={undefined}>
          <FloatingField.Label>Purpose</FloatingField.Label>
          <FloatingField.Trix control={control} name="purpose" hideToolbar />
        </FloatingField>
      )}
      <FloatingField name="additionalInfo" errors={undefined}>
        <FloatingField.Label>Additional Info</FloatingField.Label>
        <FloatingField.Trix control={control} name="additionalInfo" hideToolbar />
      </FloatingField>
    </VStack>
  );
}
